import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Stack,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
} from '@mui/material';
import { LoginIcon, VisibilityIcon as Visibility, VisibilityOffIcon as VisibilityOff } from '@atlas/ui';
import { useAuth } from '../contexts/AuthContext';
import type { LoginResult } from '../contexts/AuthContext';
import { request, API_BASE } from '../services/api';
import { safeReturnUrl, SSO_RETURN_STORAGE_KEY } from '../utils/returnUrl';

export function LoginPage() {
  const { t } = useTranslation();
  usePageTitle(t('auth.login.title'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || '/';

  // A failed / throttled Microsoft sign-in redirects back here with `?sso=<code>`.
  // Surface it as a friendly banner instead of leaving the user on a blank form.
  // 'rate_limited' is a soft warning (retry later); the rest are hard errors.
  const ssoCode = new URLSearchParams(location.search).get('sso');
  const ssoMessage = ssoCode
    ? t([`auth.login.ssoErrors.${ssoCode}`, 'auth.login.ssoErrors.error'])
    : '';
  const ssoSeverity = ssoCode === 'rate_limited' ? 'warning' : 'error';

  // Is Microsoft Entra SSO configured on the server? Drives the button below.
  useEffect(() => {
    let active = true;
    request<{ enabled: boolean }>('/auth/entra/status')
      .then((data) => { if (active) setSsoEnabled(!!data.enabled); })
      .catch(() => { /* SSO simply stays hidden if the probe fails */ });
    return () => { active = false; };
  }, []);

  // Full-page navigate to the backend, which drives the OIDC round-trip and
  // redirects back with the session cookie set. Carry the return target (a
  // validated cross-subdomain URL if present, else the in-app `from`) so the
  // server can send the browser onward after login.
  const handleMicrosoftLogin = () => {
    const returnTo = safeReturnUrl() || from;
    const url = `${API_BASE}/auth/entra/login?return=${encodeURIComponent(returnTo)}`;
    window.location.assign(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Cross-subdomain SSO: a validated `?return=` URL takes precedence over the
      // in-app `from`. Stash it so it survives the 2FA detour (challenge/enroll
      // navigate away from this page); the destination page reads + clears it.
      const ssoReturn = safeReturnUrl();
      const result: LoginResult = await login(email, password);
      if (result.kind === 'twoFactorRequired') {
        if (ssoReturn) sessionStorage.setItem(SSO_RETURN_STORAGE_KEY, ssoReturn);
        navigate('/2fa/challenge', { state: { from }, replace: true });
      } else if (result.kind === 'mustEnrollTwoFactor') {
        if (ssoReturn) sessionStorage.setItem(SSO_RETURN_STORAGE_KEY, ssoReturn);
        navigate('/2fa/enroll', { state: { from }, replace: true });
      } else if (ssoReturn) {
        // Direct (no-2FA) login: leave the SPA for the originating subdomain.
        window.location.assign(ssoReturn);
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.login.unknownError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 420, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" fontWeight={600} gutterBottom>
            {t('auth.login.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {t('auth.login.subtitle')}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 3 }}>
            {t('auth.login.orgIdentity')}
          </Typography>

          {ssoMessage && (
            <Alert severity={ssoSeverity} sx={{ mb: 3 }}>
              {ssoMessage}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <TextField
                id="email"
                label={t('auth.login.emailLabel')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.login.emailPlaceholder')}
                disabled={isLoading}
                required
                fullWidth
                size="medium"
                autoComplete="username"
              />

              <TextField
                id="password"
                label={t('auth.login.passwordLabel')}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={isLoading}
                required
                fullWidth
                size="medium"
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(p => !p)}
                        edge="end"
                        aria-label={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={isLoading || !email.trim() || !password}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
              >
                {isLoading ? t('auth.login.submittingButton') : t('auth.login.submitButton')}
              </Button>
            </Stack>
          </form>

          {ssoEnabled && (
            <Box sx={{ mt: 2.5 }}>
              <Divider sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('auth.login.ssoDivider')}
                </Typography>
              </Divider>
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={handleMicrosoftLogin}
                disabled={isLoading}
              >
                {t('auth.login.ssoMicrosoftButton')}
              </Button>
            </Box>
          )}

          <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2, mt: 2.5 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t('auth.login.noAccountTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('auth.login.noAccountBody')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
